import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Shield, Loader2, Ban, RotateCcw, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api, getApiErrorMessage } from '@/lib/api'
import { getAdminRole, canAccess, type AdminRole } from '@/lib/auth'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'
import { format } from 'date-fns'

// CHECKPOINT: PRD-10 FR-10.12.2 — Account list row shape returned by /admin/accounts
interface Account {
  id: string
  email: string
  display_name: string | null
  plan: string
  status: string
  email_verified: boolean
  domain_count: number
  mailbox_count: number
  last_active: string | null
  created_at: string
}

// CHECKPOINT: PRD-10 FR-10.12.2 — Account list page
export function Accounts() {
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [role, setRole] = useState<AdminRole | null>(null)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const LIMIT = 50

  useEffect(() => {
    setRole(getAdminRole())
  }, [])

  const canSuspend = canAccess(role, 'abuse_analyst')

  // CHECKPOINT: PRD-01 FR-1.9.1 — List accounts using backend page/per_page protocol
  const fetchAccounts = async (newOffset = offset) => {
    setLoading(true)
    try {
      const page = Math.floor(newOffset / LIMIT) + 1
      const res = await api.get('/admin/accounts', {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          plan: planFilter || undefined,
          page,
          per_page: LIMIT,
        },
      })
      setAccounts(res.data.accounts || [])
      setTotal(res.data.pagination?.total || 0)
      setOffset(newOffset)
    } catch {
      toast.error('Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccounts(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchAccounts(0)
  }

  const handlePageChange = (newOffset: number) => {
    fetchAccounts(newOffset)
  }

  const handleSuspend = async (id: string) => {
    setActionLoading(id)
    try {
      await api.post(`/admin/accounts/${id}/suspend`, { reason: 'Suspended by admin' })
      toast.success('Account suspended')
      fetchAccounts()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to suspend'))
    } finally {
      setActionLoading(null)
    }
  }

  const handleRestore = async (id: string) => {
    setActionLoading(id)
    try {
      await api.post(`/admin/accounts/${id}/restore`)
      toast.success('Account restored')
      fetchAccounts()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to restore'))
    } finally {
      setActionLoading(null)
    }
  }

  // CHECKPOINT: PRD-01 FR-1.9.2 — Open dashboard impersonation URL returned by the backend
  const handleImpersonate = async (id: string) => {
    try {
      const res = await api.post(`/admin/impersonate/${id}`)
      const dashboardUrl = res.data.dashboard_url
      if (dashboardUrl) {
        window.open(dashboardUrl, '_blank', 'noopener,noreferrer')
        toast.success('Dashboard opened in new tab')
      } else {
        toast.error('Impersonation URL missing')
      }
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to impersonate'))
    }
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/10 text-emerald-500'
      case 'suspended': return 'bg-red-500/10 text-red-500'
      case 'pending': return 'bg-amber-500/10 text-amber-500'
      default: return 'bg-gray-500/10 text-gray-500'
    }
  }

  const formatDate = (value: string | null) => {
    if (!value) return '—'
    try {
      return format(new Date(value), 'MMM d, yyyy HH:mm')
    } catch {
      return value
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Accounts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage customer accounts</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="status-filter" className="text-xs text-muted-foreground">Status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="plan-filter" className="text-xs text-muted-foreground">Plan</label>
          <select
            id="plan-filter"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors"
          >
            <option value="">All</option>
            <option value="starter">Starter</option>
            <option value="builder">Builder</option>
          </select>
        </div>
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Email</th>
                  <th className="px-4 py-3 text-left font-medium">Plan</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Domains</th>
                  <th className="px-4 py-3 text-left font-medium">Mailboxes</th>
                  <th className="px-4 py-3 text-left font-medium">Last active</th>
                  <th className="px-4 py-3 text-left font-medium">Created</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : accounts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                      No accounts found
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/accounts/${account.id}`)}
                          className="text-primary hover:underline font-medium"
                        >
                          {account.email}
                        </button>
                        {account.display_name && (
                          <div className="text-muted-foreground text-xs">{account.display_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 capitalize">{account.plan}</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={statusColor(account.status)}>
                          {account.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">{account.domain_count}</td>
                      <td className="px-4 py-3">{account.mailbox_count}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDate(account.last_active)}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(account.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Impersonate"
                            onClick={() => handleImpersonate(account.id)}
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                          {canSuspend &&
                            (account.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Suspend"
                                onClick={() => handleSuspend(account.id)}
                                disabled={actionLoading === account.id}
                              >
                                {actionLoading === account.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Ban className="h-4 w-4 text-red-500" />
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Restore"
                                onClick={() => handleRestore(account.id)}
                                disabled={actionLoading === account.id}
                              >
                                {actionLoading === account.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4 text-emerald-500" />
                                )}
                              </Button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={handlePageChange} />
      </Card>
    </div>
  )
}
